import React, { createContext, useContext, useMemo } from 'react'
import { en, type TranslationKey } from './en'
import { es } from './es'
import { esPE } from './es-PE'
import { esEC } from './es-EC'
import type { Locale } from './locales'
import { getLocaleFallbackChain } from './locales'

type I18nContextValue = {
  locale: Locale
  t: (key: TranslationKey, vars?: Record<string, string | number>) => string
}

const I18nContext = createContext<I18nContextValue | null>(null)

const dictForLocale = (locale: Locale): Partial<Record<TranslationKey, string>> => {
  switch (locale) {
    case 'es-PE':
      return esPE
    case 'es-EC':
      return esEC
    case 'es':
      return es
    case 'en':
    default:
      return en
  }
}

const resolveString = (locale: Locale, key: TranslationKey): string => {
  const chain = getLocaleFallbackChain(locale)
  for (const candidate of chain) {
    const dict = dictForLocale(candidate)
    const value = dict[key]
    if (typeof value === 'string' && value.length > 0) return value
  }
  return en[key]
}

const interpolateVars = (template: string, vars?: Record<string, string | number>): string => {
  if (!vars) return template
  let out = template
  for (const [name, value] of Object.entries(vars)) {
    out = out.replaceAll(`{${name}}`, String(value))
  }
  return out
}

export const I18nProvider: React.FC<React.PropsWithChildren<{ locale: Locale }>> = ({ locale, children }) => {
  const value = useMemo<I18nContextValue>(() => {
    return {
      locale,
      t: (key, vars) => interpolateVars(resolveString(locale, key), vars)
    }
  }, [locale])

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export const useT = (): ((key: TranslationKey, vars?: Record<string, string | number>) => string) => {
  const ctx = useContext(I18nContext)
  if (!ctx) {
    return (key: TranslationKey, vars?: Record<string, string | number>) => interpolateVars(en[key], vars)
  }
  return ctx.t
}

export const useLocale = (): Locale => {
  const ctx = useContext(I18nContext)
  return ctx?.locale ?? 'en'
}
